import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import RecordingCard from "./components/RecordingCard";
import RecordingControls from "./components/RecordingControls";
import ProfileDropdown from "../shared/components/ProfileDropdown";
import SignInPill from "../shared/components/SignInPill";
import { DocumentService, LocalDocument } from "../shared/services/DocumentService";
import supabase from "../shared/supabase/supabase";
import { Tables } from "../shared/supabase/database.types";

type CloudDocument = Tables<"Documents">;
type Document = CloudDocument | LocalDocument;

interface Recording {
  id: string;
  title: string;
  duration: string;
  time: string;
  date: string;
  preview: string;
}

const RecordingsScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Selection state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    try {
      const docs = await DocumentService.getDocuments();
      setDocuments(docs as Document[]);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load on mount
  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = await DocumentService.isAuthenticated();
      setIsAuthenticated(isAuth);
      
      if (isAuth) {
        const { data: { user } } = await supabase.auth.getUser();
        setUserEmail(user?.email || null);
      }
    };
    
    checkAuth();
    loadDocuments();
  }, [loadDocuments]);

  // Subscribe to auth changes separately
  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = await DocumentService.isAuthenticated();
      setIsAuthenticated(isAuth);
      
      if (isAuth) {
        const { data: { user } } = await supabase.auth.getUser();
        setUserEmail(user?.email || null);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        checkAuth();
        // Only load documents on SIGNED_OUT or if we have none
        if (event === 'SIGNED_OUT' || documents.length === 0) {
          loadDocuments();
        }
      } else if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        // Just update auth state, don't reload documents
        checkAuth();
      }
    });
    
    return () => subscription.unsubscribe();
  }, [loadDocuments, documents.length]);

  const handleDelete = useCallback(async (id: string) => {
    const success = await DocumentService.deleteDocument(id);
    if (success) {
      // Remove from local state with animation
      setDocuments(prev => prev.filter(doc => {
        if ('localId' in doc) {
          return doc.localId !== id;
        } else {
          return doc.id !== id;
        }
      }));
    }
    return success;
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;

    const idsToDelete = Array.from(selectedIds);

    // Optimistic UI update
    setDocuments(prev => prev.filter(doc => {
      const docId = 'localId' in doc ? doc.localId : doc.id;
      return !selectedIds.has(docId);
    }));
    setShowDeleteModal(false);
    setSelectedIds(new Set());

    try {
      // Use RPC for atomic bulk delete
      const { error } = await supabase.rpc('bulk_delete_documents', {
        document_ids: idsToDelete
      });

      if (error) {
        console.error('Bulk delete failed:', error);
        // Reload documents to restore state on error
        await loadDocuments();
      }
    } catch (error) {
      console.error('Bulk delete failed:', error);
      await loadDocuments();
    }
  }, [selectedIds, loadDocuments]);

  // Strip markdown formatting for preview display
  const stripMarkdown = (markdown: string): string => {
    return markdown
      // Remove code blocks first (before other replacements)
      .replace(/```[\s\S]*?```/g, '')
      // Remove headers
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold/italic
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      .replace(/(\*|_)(.*?)\1/g, '$2')
      // Remove links but keep text
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
      // Remove images
      .replace(/!\[([^\]]*)\]\([^\)]+\)/g, '')
      // Remove inline code backticks
      .replace(/`([^`]+)`/g, '$1')
      // Remove blockquotes
      .replace(/^\s*>\s+/gm, '')
      // Remove horizontal rules
      .replace(/^(-{3,}|\*{3,}|_{3,})$/gm, '')
      // Remove list markers
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      // Remove ALL backslashes (escape characters)
      .replace(/\\/g, '')
      // Clean up multiple blank lines but preserve single newlines
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Convert documents to recordings format
  const recordings: Recording[] = useMemo(() => {
    return documents.map(doc => {
      const id = 'localId' in doc ? doc.localId : doc.id;
      const createdAt = new Date('created_at' in doc ? doc.created_at || new Date().toISOString() : doc.createdAt);
      const updatedAt = new Date('updated_at' in doc ? doc.updated_at || new Date().toISOString() : doc.updatedAt);
      
      // Format date
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      let date = "";
      if (createdAt.toDateString() === today.toDateString()) {
        date = "Today";
      } else if (createdAt.toDateString() === yesterday.toDateString()) {
        date = "Yesterday";
      } else {
        date = createdAt.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        });
      }
      
      // Format time
      const time = createdAt.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      // Calculate duration (mock for now)
      const duration = Math.floor(Math.random() * 10) + ":" + 
                      String(Math.floor(Math.random() * 60)).padStart(2, '0');
      
      // Get preview from edited text or original text, strip markdown formatting
      const content = ('edited_text' in doc ? doc.edited_text : doc.editedText) ||
                     ('original_text' in doc ? doc.original_text : doc.originalText) || '';
      const plainText = stripMarkdown(content);
      const preview = plainText.slice(0, 200) + (plainText.length > 200 ? '...' : '');
      
      return {
        id,
        title: doc.title || "Untitled",
        duration,
        time,
        date,
        preview
      };
    });
  }, [documents]);

  // Fuzzy search implementation
  const filteredRecordings = useMemo(() => {
    if (!searchQuery.trim()) return recordings;

    const query = searchQuery.toLowerCase();
    const words = query.split(/\s+/);

    return recordings
      .filter((recording) => {
        const searchableText =
          `${recording.title} ${recording.preview}`.toLowerCase();

        // Check if all words are found somewhere in the text
        return words.every((word) => searchableText.includes(word));
      })
      .sort((a, b) => {
        // Sort by relevance: prioritize title matches
        const aTitle = a.title.toLowerCase();
        const bTitle = b.title.toLowerCase();
        const aTitleMatch = words.filter((word) =>
          aTitle.includes(word)
        ).length;
        const bTitleMatch = words.filter((word) =>
          bTitle.includes(word)
        ).length;

        if (aTitleMatch !== bTitleMatch) {
          return bTitleMatch - aTitleMatch;
        }

        // Then sort by date (most recent first)
        return recordings.indexOf(a) - recordings.indexOf(b);
      });
  }, [searchQuery, recordings]);

  const groupedRecordings = filteredRecordings.reduce((groups, recording) => {
    const date = recording.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(recording);
    return groups;
  }, {} as Record<string, Recording[]>);

  return (
    <div className="min-h-screen bg-gray-50 pb-40">
      {/* Header with User Info and Logout */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
        }}
        className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100"
      >
        <div className="px-3 sm:px-6 md:px-12 py-2 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
          {/* Logo/Title */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center justify-center sm:justify-start space-x-2 sm:space-x-3 w-full sm:w-auto"
          >
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-sky-400 to-sky-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs sm:text-sm">W</span>
            </div>
            <h1 className="text-base sm:text-lg font-semibold text-gray-900">WriteVoice</h1>
          </motion.div>

          {/* Right side - Voice Guide pill + Sign-in pill or Profile */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto justify-center sm:justify-start"
          >
            <motion.a
              href="https://wrpsotblncefcetxvkth.supabase.co/storage/v1/object/public/app-resources/guide/writevoice-voice-guide.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="vg-pill flex items-center gap-1.5 px-3.5 py-1.5 text-white text-xs font-semibold rounded-full select-none cursor-pointer"
              whileHover={{ scale: 1.05, boxShadow: "0 4px 16px rgba(14,165,233,0.5)" }}
              whileTap={{ scale: 0.96 }}
            >
              <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"/>
                <path d="M19 10a7 7 0 0 1-14 0"/>
                <line x1="12" y1="19" x2="12" y2="22"/>
              </svg>
              Voice Guide
            </motion.a>
            {isAuthenticated ? (
              <ProfileDropdown email={userEmail} />
            ) : (
              <SignInPill />
            )}
          </motion.div>
        </div>
      </motion.header>

      <div className="px-4 sm:px-6 md:px-12 py-4 sm:py-6 md:py-8">
        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
          }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="relative group">
            <motion.div
              animate={{
                scale: isFocused ? 1.01 : 1,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 25,
              }}
              className="relative"
            >
              {/* Search Icon */}
              <motion.div
                animate={{
                  x: isFocused ? 4 : 0,
                  opacity: isFocused ? 0.7 : 1,
                }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                }}
                className="absolute left-6 inset-y-0 flex items-center pointer-events-none"
              >
                <Search className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
              </motion.div>

              {/* Input Field */}
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder="Search recordings..."
                className="w-full pl-14 pr-12 py-4 bg-white rounded-2xl border border-gray-200 
                         focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10
                         text-base text-gray-900 placeholder:text-gray-400
                         transition-all duration-200 leading-normal"
                style={{
                  boxShadow: isFocused
                    ? "0 10px 40px -10px rgba(14, 165, 233, 0.15), 0 0 0 1px rgba(14, 165, 233, 0.1)"
                    : "0 2px 8px -2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.02)",
                }}
              />

              {/* Clear Button */}
              <AnimatePresence>
                {searchQuery && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setSearchQuery("")}
                    className="absolute right-6 top-1/2 -translate-y-1/2 w-6 h-6 
                             flex items-center justify-center rounded-full
                             bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-gray-600" strokeWidth={2} />
                  </motion.button>
                )}
              </AnimatePresence>

              {/* Search Results Count */}
              <AnimatePresence>
                {searchQuery && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute -bottom-6 left-0 text-xs text-gray-500"
                  >
                    {filteredRecordings.length === 0 ? (
                      <span>No results found</span>
                    ) : (
                      <span>
                        {filteredRecordings.length} result
                        {filteredRecordings.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Subtle gradient line under search when focused */}
            <motion.div
              className="absolute -bottom-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-500 to-transparent"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{
                scaleX: isFocused ? 1 : 0,
                opacity: isFocused ? 0.5 : 0,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 25,
              }}
            />
          </div>
        </motion.div>

        {/* Selection Toolbar - Shows when at least 1 card is selected */}
        <motion.div
          initial={false}
          animate={{
            height: selectedIds.size > 0 ? 'auto' : 0,
            marginTop: selectedIds.size > 0 ? 16 : 0,
            opacity: selectedIds.size > 0 ? 1 : 0,
          }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="max-w-4xl mx-auto overflow-hidden"
        >
          <div className="bg-white border border-gray-200 rounded-2xl px-6 py-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Select All Checkbox */}
                <button
                  onClick={() => {
                    if (selectedIds.size === filteredRecordings.length) {
                      setSelectedIds(new Set());
                    } else {
                      setSelectedIds(new Set(filteredRecordings.map(r => r.id)));
                    }
                  }}
                  className="flex items-center space-x-2 group"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    selectedIds.size === filteredRecordings.length
                      ? 'bg-sky-500 border-sky-500'
                      : 'border-gray-300 group-hover:border-sky-500'
                  }`}>
                    {selectedIds.size === filteredRecordings.length && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700">Select All</span>
                </button>

                {/* Selected Count */}
                <span className="text-xs text-gray-500">
                  {selectedIds.size} selected
                </span>
              </div>

              <div className="flex items-center space-x-3">
                {/* Delete Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowDeleteModal(true)}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-sm font-medium transition-all shadow-md flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Delete ({selectedIds.size})</span>
                </motion.button>

                {/* Cancel Button */}
                <button
                  onClick={() => {
                    setSelectedIds(new Set());
                  }}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div className="space-y-8 mt-16 pb-8" initial={false}>
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-20"
              >
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
                </div>
                <p className="text-gray-500 text-sm">Loading recordings...</p>
              </motion.div>
            ) : documents.length === 0 && !searchQuery ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                }}
                className="flex flex-col items-center justify-center py-20"
              >
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3,
                  }}
                  className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-50 to-sky-100 flex items-center justify-center mb-4"
                >
                  <div className="w-3 h-3 bg-sky-500 rounded-full" />
                </motion.div>
                <p className="text-gray-900 font-medium text-base mb-1">No recordings yet</p>
                <p className="text-gray-500 text-sm">Start recording to see your content here</p>
              </motion.div>
            ) : Object.keys(groupedRecordings).length === 0 && searchQuery ? (
              <motion.div
                key="no-results"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 20,
                }}
                className="flex flex-col items-center justify-center py-20"
              >
                <motion.div
                  animate={{
                    rotate: [0, -5, 5, -5, 0],
                    scale: [1, 1.05, 1, 1.05, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3,
                  }}
                  className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4"
                >
                  <Search className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
                </motion.div>
                <p className="text-gray-500 text-sm">
                  No recordings match your search
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Try different keywords
                </p>
              </motion.div>
            ) : (
              Object.entries(groupedRecordings).map(
                ([date, dateRecordings], groupIndex) => (
                  <motion.div
                    key={date}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      layout: {
                        type: "spring",
                        stiffness: 200,
                        damping: 25,
                      },
                      opacity: {
                        duration: 0.2,
                        delay: groupIndex * 0.03,
                      },
                    }}
                  >
                    <motion.h2
                      className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-4"
                      layout="position"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 200,
                        damping: 20,
                        delay: groupIndex * 0.03,
                      }}
                    >
                      {date}
                    </motion.h2>
                    <motion.div
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                      layout
                    >
                      <AnimatePresence>
                        {dateRecordings.map((recording, index) => (
                          <motion.div
                            key={recording.id}
                            layout
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: -20 }}
                            transition={{
                              layout: {
                                type: "spring",
                                stiffness: 300,
                                damping: 30,
                              },
                              opacity: {
                                duration: 0.2,
                              },
                              scale: {
                                type: "spring",
                                stiffness: 200,
                                damping: 20,
                                delay: index * 0.02,
                              },
                            }}
                            className="min-w-0 relative"
                            onMouseEnter={() => setHoveredCardId(recording.id)}
                            onMouseLeave={() => setHoveredCardId(null)}
                          >
                            {/* Selection Checkbox + Click to Edit - Shows on hover or when selected */}
                            <AnimatePresence>
                              {(hoveredCardId === recording.id || selectedIds.has(recording.id)) && (
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0, scale: 0.8 }}
                                  transition={{ duration: 0.2 }}
                                  className="absolute top-4 left-4 z-50 flex items-center space-x-2"
                                  style={{ pointerEvents: 'auto' }}
                                >
                                  <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const newSelected = new Set(selectedIds);
                                      if (newSelected.has(recording.id)) {
                                        newSelected.delete(recording.id);
                                      } else {
                                        newSelected.add(recording.id);
                                      }
                                      setSelectedIds(newSelected);
                                    }}
                                    className={`w-9 h-9 rounded-lg border-2 flex items-center justify-center transition-all shadow-xl ${
                                      selectedIds.has(recording.id)
                                        ? 'bg-sky-500 border-sky-500'
                                        : 'bg-white border-gray-400 hover:border-sky-500 hover:bg-sky-50'
                                    }`}
                                  >
                                    {selectedIds.has(recording.id) ? (
                                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                      </svg>
                                    ) : (
                                      <div className="w-4 h-4 rounded border-2 border-gray-400" />
                                    )}
                                  </motion.button>

                                  {/* Click to Edit hint */}
                                  {!selectedIds.has(recording.id) && (
                                    <motion.button
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/editor?mode=edit&id=${encodeURIComponent(recording.id)}&title=${encodeURIComponent(recording.title)}&content=${encodeURIComponent(recording.preview)}`);
                                      }}
                                      className="flex items-center space-x-1.5 text-xs text-sky-600 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md hover:bg-sky-50 hover:shadow-lg transition-all"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                      <span className="font-medium">Click to edit</span>
                                    </motion.button>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>
                            <RecordingCard
                              id={recording.id}
                              title={recording.title}
                              duration={recording.duration}
                              time={recording.time}
                              preview={recording.preview}
                              onDelete={handleDelete}
                            />
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </motion.div>
                  </motion.div>
                )
              )
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Recording Controls - Always show */}
      <RecordingControls />

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete documents?</h3>
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete {selectedIds.size} {selectedIds.size === 1 ? 'document' : 'documents'}? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex space-x-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleBulkDelete}
                  className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium transition-all shadow-lg"
                >
                  Delete
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RecordingsScreen;
